if (!customElements.get('wellness-blog-slider')) {
  customElements.define('wellness-blog-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-blog-track]');
      this.list = this.querySelector('[data-blog-list]');
      this.cards = Array.from(this.track?.children || []);
      if (!this.cards.length) return;
      this.index = 0;
      this.moving = false;
      this.hovered = false;
      this.touchStart = null;
      this.paused = false;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visibleRows = this.rowCount();
      this.cards.forEach((card, index) => {
        const item = card.cloneNode(true);
        item.classList.add('wellness-blog__compact');
        item.dataset.articleIndex = index;
        item.tabIndex = 0;
        item.setAttribute('aria-label', card.querySelector('h3').textContent.trim());
        this.list.append(item);
      });
      // Extra end cards keep both tracks moving forwards across the loop boundary.
      if (this.cards.length > 1) {
        this.track.append(this.cards[0].cloneNode(true));
        Array.from(this.list.children).slice(0, Math.min(5, this.cards.length)).forEach(card => this.list.append(card.cloneNode(true)));
      }
      this.setAttribute('data-ready', '');
      this.querySelector('[data-blog-prev]')?.addEventListener('click', () => this.manualMove(-1), options);
      this.querySelector('[data-blog-next]')?.addEventListener('click', () => this.manualMove(1), options);
      this.querySelector('[data-blog-toggle]')?.addEventListener('click', event => {
        this.paused = !this.paused;
        event.currentTarget.setAttribute('aria-pressed', String(this.paused));
        const label = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
        event.currentTarget.setAttribute('aria-label', label);
        event.currentTarget.querySelector('[data-blog-toggle-label]').textContent = label;
        this.start();
      }, options);
      this.addEventListener('pointerenter', () => { this.hovered = true; this.stop(); }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.start(); }, options);
      this.addEventListener('focusin', () => this.stop(), options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.start(); }, options);
      this.list.addEventListener('click', event => this.selectArticle(event), options);
      this.list.addEventListener('keydown', event => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-article-index]')) {
          event.preventDefault();
          this.selectArticle(event);
        }
      }, options);
      this.addEventListener('pointerdown', event => {
        this.verticalSwipe = Boolean(event.target.closest('[data-blog-list]'));
        this.touchStart = event.pointerType === 'touch' ? (this.verticalSwipe ? event.clientY : event.clientX) : null;
        this.stop();
      }, options);
      this.addEventListener('pointerup', event => {
        const end = this.verticalSwipe ? event.clientY : event.clientX;
        if (this.touchStart !== null && Math.abs(end - this.touchStart) > 45) this.move(end < this.touchStart ? 1 : -1);
        this.touchStart = null;
        this.start();
      }, options);
      this.addEventListener('pointercancel', () => { this.touchStart = null; this.start(); }, options);
      this.track.addEventListener('transitionend', event => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishMove();
      }, options);
      this.reducedMotion.addEventListener('change', () => { this.finishMove(); this.start(); }, options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      this.resize = new ResizeObserver(() => this.render(false));
      this.resize.observe(this.querySelector('.wellness-blog__featured'));
      this.render(false);
      this.start();
    }
    rowCount() {
      const rows = matchMedia("(max-width: 991px)").matches ? 2 : matchMedia("(max-width: 1199px)").matches ? 4 : 5;
      return Math.min(rows, this.cards.length);
    }
    render(animate = true) {
      this.visibleRows = this.rowCount();
      const rowHeight = Math.min(136, this.querySelector('.wellness-blog__featured').getBoundingClientRect().height / Math.min(5, this.cards.length));
      this.style.setProperty('--blog-row-height', `${rowHeight}px`);
      this.style.setProperty('--blog-list-height', `${rowHeight * this.visibleRows}px`);
      [this.track, this.list].forEach(track => track.classList.toggle('is-resetting', !animate || this.reducedMotion.matches));
      this.track.style.transform = `translateX(${-this.index * 100}%)`;
      this.list.style.transform = `translateY(${-this.index * rowHeight}px)`;
      const mobile = matchMedia('(max-width: 749px)').matches;
      Array.from(this.track.children).forEach((card, index) => {
        card.inert = index !== this.index;
        card.setAttribute('aria-hidden', String(index !== this.index));
      });
      Array.from(this.list.children).forEach((card, index) => {
        const visible = !mobile && index >= this.index && index < this.index + this.visibleRows;
        card.inert = !visible;
        card.setAttribute('aria-hidden', String(!visible));
      });
    }
    move(direction) {
      if (this.cards.length < 2 || this.moving) return;
      if (direction < 0 && this.index === 0) {
        this.index = this.cards.length;
        this.render(false);
        // Commit the identical end frame before sliding back to the last article.
        void this.track.offsetWidth;
      }
      this.index += direction;
      this.moving = true;
      this.render();
      clearTimeout(this.motionTimer);
      if (this.reducedMotion.matches) this.finishMove();
      else this.motionTimer = setTimeout(() => this.finishMove(), 1100);
    }
    finishMove() {
      clearTimeout(this.motionTimer);
      if (this.index === this.cards.length) {
        this.index = 0;
        this.render(false);
      }
      this.moving = false;
    }
    selectArticle(event) {
      if (event.target.closest('a') || this.moving) return;
      const card = event.target.closest('[data-article-index]');
      if (!card) return;
      this.index = Number(card.dataset.articleIndex);
      this.render();
      this.start();
    }
    manualMove(direction) { this.move(direction); this.start(); }
    start() {
      this.stop();
      if (this.cards.length > 1 && !this.paused && !this.hovered && !this.reducedMotion.matches && this.dataset.autoplay === 'true' && !document.hidden && !this.contains(document.activeElement)) {
        this.timer = setInterval(() => this.move(1), Math.max(3, Number(this.dataset.delay) || 5) * 1000);
      }
    }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() {
      this.stop();
      clearTimeout(this.motionTimer);
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
      this.track?.replaceChildren(...this.cards);
      this.list?.replaceChildren();
      this.removeAttribute('data-ready');
    }
  });
}
